import { useState, useCallback, useEffect, useRef } from 'react';
import { Station } from '../../types/station';
import stationsService from '../../services/ScaleoAdminApiServices/StationsService';
import recognitionsService from '../../services/ScaleoAdminApiServices/RecognitionsService';
import { Recognition } from '../../types/recognition';

export const useRecognitionsData = () => {
  const [componentData, setComponentData] = useState<{
    recognitions: Recognition[];
    stations: Station[];
  }>({
    recognitions: [],
    stations: [],
  });
  const [filters, setFilters] = useState<any>({});
  const [internalFilters, setInternalFilters] = useState(filters);
  const [paginationParameters, setPaginationParameters] = useState(emptyPaginationParameters);
  const [paginatorState, setPaginatorState] = useState(emptyPaginatorState);
  const debounceTimerRef = useRef<any>(null);

  const fetchRecognitions = useCallback(
    async (_filters: any, _paginationParameters: any) => {
      void Promise.all([
        recognitionsService.searchRecognitions(_filters, _paginationParameters),
        stationsService.getStations(),
      ]).then((response) => {
        setComponentData({
          recognitions: response[0].data.data,
          stations: response[1].data.map((s: Station) => ({
            ...s,
            nameInstallationId: s.installationId ? `${s.name} [${s.installationId}]` : s.name,
          })),
        });

        setPaginatorState({
          ...paginatorState,
          totalPages: response[0].data.totalPagesNumber,
          currentPage: paginatorState.currentPage > response[0].data.totalPagesNumber ? 1 : paginatorState.currentPage,
        });
      });
    },
    [
      recognitionsService.searchRecognitions,
      stationsService.getStations,
      setComponentData,
      setPaginatorState,
      JSON.stringify(paginatorState),
    ],
  );

  useEffect(() => {
    if (typeof internalFilters?.station !== 'string') {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        void fetchRecognitions(internalFilters, paginationParameters);
      }, 100);
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [JSON.stringify(internalFilters), JSON.stringify(paginationParameters)]);

  useEffect(() => {
    if (
      paginatorState.currentPage !== paginationParameters.page.index ||
      paginatorState.rows !== paginationParameters.page.size
    ) {
      const params = {
        ...paginationParameters,
        page: {
          index: paginatorState.currentPage,
          size: paginatorState.rows,
        },
      };
      setPaginationParameters(params);
    }
  }, [paginationParameters, paginatorState]);

  const handleSort = (e: any) => {
    setPaginationParameters({
      ...paginationParameters,
      order: {
        isAscending: e.sortOrder === 1 ? true : false,
        orderColumn: e.sortField,
      },
    });
  };

  const changeFilter = (value: any, name: string) => {
    const newFilters: any = { ...internalFilters };
    newFilters[name] = value;

    if (name === 'station') {
      if (typeof value !== 'string') {
        newFilters.stationId = value?.id || null;
      }
    }

    setFilters(newFilters);
    setInternalFilters(newFilters);
  };

  const clearFilters = () => {
    setFilters({});
    setInternalFilters({});
  };

  const refresh = () => {
    void fetchRecognitions(internalFilters, paginationParameters);
  };

  return {
    recognitions: componentData.recognitions,
    stations: componentData.stations,
    handleSort,
    paginationParameters,
    paginatorState,
    setPaginatorState,
    internalFilters,
    changeFilter,
    clearFilters,
    refresh,
    filters,
  };
};

const emptyPaginationParameters = {
  order: {
    isAscending: false,
    orderColumn: 'createdAt',
  },
  page: {
    index: 1,
    size: 10,
  },
};

const emptyPaginatorState = {
  currentPage: 1,
  totalPages: 1,
  rows: 10,
};
